import React, { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact square swatch image picker.
 * Props:
 *  - value: storage path (string) or null
 *  - onChange: (path | null) => void
 *  - testid: data-testid prefix
 *  - label
 */
export const ImagePicker = ({ value, onChange, testid = "image-picker", label = "Swatch image" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange && onChange(data.path);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeImg = (e) => {
    e.stopPropagation();
    onChange && onChange(null);
  };

  return (
    <div className="space-y-1.5" data-testid={testid}>
      {label && (
        <span className="text-xs uppercase tracking-wider text-brand-700/70 font-medium block">
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="relative w-full aspect-square max-w-[160px] rounded-2xl border-2 border-dashed border-brand-300 bg-brand-50 overflow-hidden flex items-center justify-center transition-colors hover:border-brand-500"
        data-testid={`${testid}-button`}
      >
        {uploading ? (
          <Loader2 className="w-7 h-7 animate-spin text-brand-700/60" />
        ) : value ? (
          <>
            <img
              src={fileUrl(value)}
              alt="Swatch"
              className="w-full h-full object-cover"
              data-testid={`${testid}-preview`}
            />
            <span
              role="button"
              tabIndex={0}
              onClick={removeImg}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && removeImg(e)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center"
              data-testid={`${testid}-remove`}
              aria-label="Remove image"
            >
              <X className="w-4 h-4 text-brand-900" strokeWidth={2} />
            </span>
          </>
        ) : (
          <div className="flex flex-col items-center text-brand-700/50 gap-1.5">
            <Camera className="w-7 h-7" strokeWidth={1.5} />
            <span className="text-xs font-medium">Tap to add</span>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testid}-input`}
      />
    </div>
  );
};
